import ClassNames from "classnames";
import { FunctionComponent, useEffect, useState } from "react";
import { getActiveSite } from "../../selectors/sites.js";
import { injectScript } from "../../slices/loadStates.js";
import { useAppDispatch, useAppSelector } from "../../store/hooks.js";
import styles from "../../stylesheets/components/guestnet-widget-module.module.css";
import moduleBaseStyles from "../../stylesheets/components/module-base.module.css";
import {
  BaseModuleProps,
  GuestnetWidgetModuleSettings,
} from "../../types/index.js";
import {
  getActiveColorScheme,
  getSupportedLanguage,
} from "../../utils/utils.js";
import LazyloadWrapper from "../LazyloadWrapper.js";
import ModuleHeadings from "../ModuleHeadings.js";
import ModuleWithHeadings from "../ModuleWithHeadings.js";

declare module "react/jsx-runtime" {
  namespace JSX {
    interface IntrinsicElements {
      "gn-weekprogram": {
        "data-base-url": string;
        "data-color": string;
        "data-lang": string;
      };
    }
  }
}

type Props = BaseModuleProps<GuestnetWidgetModuleSettings>;

const GuestnetWidgetModule: FunctionComponent<Props> = ({
  translatedModule: {
    id,
    settings: { textAlign, baseUrl },
    translation: {
      languageId,
      settings: { title, subtitle },
    },
  },
  translatedModule,
  isFirstOnPage,
}) => {
  const dispatch = useAppDispatch();
  const scheme = useAppSelector((state) =>
    getActiveColorScheme(
      state.colorSchemes,
      getActiveSite(state.sites),
      translatedModule,
    ),
  );

  const [isLazyloaded, setIsLazyloaded] = useState(false);

  useEffect(() => {
    isLazyloaded &&
      baseUrl &&
      // TODO: setTimeout is only a temporary workaround
      // to prevent the widget from messing with the
      // colors provided by Emotion. Find another
      // solution for this.
      setTimeout(() => {
        dispatch(
          injectScript(
            "https://s3.eu-central-1.amazonaws.com/guestnet.info/widgets/weekprogram-1.1.js",
          ),
        );
      }, 2000);
  }, [isLazyloaded, baseUrl]);

  return (
    <ModuleWithHeadings
      title={title}
      subtitle={subtitle}
      id={id}
      className={styles.GuestnetWidgetModule}
      colors={{ background: scheme.main.background, color: scheme.main.text }}
    >
      <div className={moduleBaseStyles.Module__Wrapper}>
        <ModuleHeadings
          scheme={scheme}
          isFirstOnPage={isFirstOnPage}
          textAlign={textAlign}
          title={title}
          subtitle={subtitle}
        />
      </div>

      <LazyloadWrapper onLoad={setIsLazyloaded}>
        <div
          className={ClassNames(
            moduleBaseStyles.Module__Wrapper,
            styles.GuestnetWidgetModule__Container,
          )}
        >
          {baseUrl && (
            <gn-weekprogram
              data-base-url={baseUrl}
              data-color={scheme.main.separator}
              data-lang={getSupportedLanguage(languageId, ["de", "it", "en"])}
            ></gn-weekprogram>
          )}
          {!baseUrl && (
            <div className={styles.GuestnetWidgetModule__Empty}>Guestnet</div>
          )}
        </div>
      </LazyloadWrapper>
    </ModuleWithHeadings>
  );
};

export default GuestnetWidgetModule;
